// Placement report — the long-scroll keepsake (HOP-27).
//
// Design source: specs/Hopscotch-UI-handoff/Learner Profile Final.html
// (screens/learner-profile.jsx, journey="vert-named", "duo" palette: blue
// takes editorial structure; coral keeps stone + identity + action).
//
// Hero: the vertical hopscotch court with named squares (chalk Caveat
// numbers, dashed unprinted futures, yellow check badges on done stages,
// the ink beanbag with yellow stitches on the current square, START mark,
// yellow sun at the top) + the identity headline with the page's ONE
// yellow swipe + both CTAs above the fold + the rotated date stamp.
// Stones are printed objects, NOT buttons (no tappability affordances) and
// never get per-stage colors (the reserved rule).
//
// Body: ~680px reading column, one carded moment ("From your placement"),
// CTA repeats at the end. 66px headline allowed here only.
//
// Stage names per HOP-28: the STAGE_DISPLAY journey names. The Code Cracker
// body copy is verbatim from the design; the other five stages are authored
// in the same voice (relief not guilt, no em dashes, decode words in
// Andika) pending content review.

import { useState, type ReactNode } from 'react'
import { Grain } from '@/components/hs/Grain'
import { STAGE_DISPLAY, stages, type StageLabel } from '@/data/stageDefinitions'
import { MaskedName } from '@/lib/privacyMask'
import './placementReport.css'

// Decode word — Andika, only for words the child reads.
function Dw({ children }: { children: ReactNode }) {
  return <span className="hs-decode pr-dw">{children}</span>
}

// ── Per-stage editorial content ─────────────────────────────────────────────

interface StageContent {
  subhead: string
  nowWorkingOn: ReactNode
  opener: ReactNode
  mastered: ReactNode
  buildingIntro: string
  building: Array<[ReactNode, ReactNode]>
  atHome: ReactNode
  booksReady: ReactNode
  ctaLabel: string
}

const STAGE_CONTENT: Record<number, StageContent> = {
  0: {
    subhead: 'The love of stories is already here.',
    nowWorkingOn: (
      <>Now working on: hearing the sounds inside spoken words and meeting the letters that make them.</>
    ),
    opener: (
      <>
        Sound Explorers are tuning their ears. They are learning that words are made of sounds, that{' '}
        <Dw>bat</Dw> and <Dw>cat</Dw> rhyme, and that those squiggles on the page are letters with names and
        sounds of their own.
      </>
    ),
    mastered: (
      <>
        Story sense comes first, and it is here: following a book, predicting what happens next, holding it the
        right way up and knowing the words run left to right. That foundation is what everything else gets
        built on.
      </>
    ),
    buildingIntro: 'Sound Explorers are learning to:',
    building: [
      [<span key="a">Hear rhyme and play with it</span>, <span key="b">knowing <Dw>hop</Dw> and <Dw>top</Dw> belong together</span>],
      [<span key="a">Clap out syllables</span>, <span key="b">breaking <Dw>butterfly</Dw> into but-ter-fly</span>],
      [<span key="a">Notice first sounds</span>, <span key="b">that <Dw>sun</Dw>, <Dw>sock</Dw>, and <Dw>sand</Dw> all start with /s/</span>],
      [<span key="a">Match letters to names and sounds</span>, <span key="b">the alphabet, one friendly letter at a time</span>],
    ],
    atHome: (
      <>
        Expect lots of word play and zero pressure to read. Rhyming games in the car, I-spy with sounds
        (something that starts with /m/), tracing letters in sand or shaving cream. Reading aloud every day
        matters more at this stage than anything else; it builds the vocabulary and story sense their own
        reading will grow into.
      </>
    ),
    booksReady: (
      <>
        Picture books with strong rhythm and rhyme are the sweet spot. Books they can memorize and "read" back
        to you are not cheating, they are rehearsal. Alphabet books earn their keep here too.
      </>
    ),
    ctaLabel: 'See books for Sound Explorers',
  },
  1: {
    subhead: 'The alphabet is locked in.',
    nowWorkingOn: (
      <>Now working on: blending sounds into first words and more (<Dw>cat</Dw>, <Dw>sun</Dw>, <Dw>mop</Dw>).</>
    ),
    opener: (
      <>
        Code Crackers know their letters and the sounds they make, and they&rsquo;re putting those sounds
        together to read their very first words, short, three-letter words like <Dw>cat</Dw>, <Dw>sun</Dw>, and{' '}
        <Dw>mop</Dw>.
      </>
    ),
    mastered: (
      <>
        A solid set of letter sounds, and the ability to hear the individual sounds inside spoken words: that{' '}
        <Dw>cat</Dw> starts with /c/, ends with /t/, and has /a/ in the middle. Those two skills are the keys to
        reading, and they are in hand.
      </>
    ),
    buildingIntro: 'Code Crackers are learning to:',
    building: [
      [<span key="a">Match letters to their sounds quickly and automatically</span>, <span key="b">so sounding out doesn&rsquo;t stall at every letter</span>],
      [<span key="a">Blend sounds into words</span>, <span key="b">like /c/ + /a/ + /t/ = <Dw>cat</Dw>, and /s/ + /u/ + /n/ = <Dw>sun</Dw></span>],
      [<span key="a">Read across all five short vowels</span>, <span key="b">working toward words like <Dw>cat</Dw> (a), <Dw>pet</Dw> (e), <Dw>sit</Dw> (i), <Dw>hot</Dw> (o), and <Dw>cup</Dw> (u)</span>],
      [<span key="a">Start reading short sentences</span>, <span key="b">once they can sound out the individual words, like <Dw>the cat ran</Dw> or <Dw>a pig is big</Dw></span>],
    ],
    atHome: (
      <>
        Code Crackers are working on turning print into language, sounding it out letter by letter. It takes
        real effort. Expect slow, careful reading: pointing to each letter, saying the sound, blending them
        together. Expect the same word to feel easy one day and hard the next; that&rsquo;s the brain building
        automatic recognition, and it takes a lot of repetition. Short <Dw>e</Dw> and short <Dw>u</Dw> are
        usually the last vowels to click.
      </>
    ),
    booksReady: (
      <>
        The best books for Code Crackers are short and simple: just a few words per page, using sounds they can
        actually work out. Rhyming books and books with repeated refrains are magic at this stage; the pattern
        and rhythm let them recognize a word once and anticipate it the next time it shows up. And don&rsquo;t
        stop reading aloud. That&rsquo;s how they build the vocabulary and story sense that will be waiting when
        their reading catches up.
      </>
    ),
    ctaLabel: 'See books for Code Crackers',
  },
  2: {
    subhead: 'Short words are sounding out smoothly.',
    nowWorkingOn: (
      <>Now working on: letter teams like <Dw>sh</Dw> and <Dw>ch</Dw>, and blends like <Dw>st</Dw> and <Dw>cl</Dw>.</>
    ),
    opener: (
      <>
        Word Builders can sound out short words on their own, and now the code gets richer: two letters that
        team up to make one sound (<Dw>ship</Dw>, <Dw>chip</Dw>, <Dw>thin</Dw>) and consonants that cluster at
        the edges of words (<Dw>clap</Dw>, <Dw>frog</Dw>, <Dw>stop</Dw>).
      </>
    ),
    mastered: (
      <>
        Blending is real: /c/ + /a/ + /t/ comes out as <Dw>cat</Dw> without a struggle, across all five short
        vowels. Sounding out is no longer the mountain it was; it is the path.
      </>
    ),
    buildingIntro: 'Word Builders are learning to:',
    building: [
      [<span key="a">Read digraphs as one sound</span>, <span key="b"><Dw>sh</Dw>, <Dw>ch</Dw>, <Dw>th</Dw>, and <Dw>wh</Dw> in words like <Dw>ship</Dw> and <Dw>chat</Dw></span>],
      [<span key="a">Hold on to blends</span>, <span key="b">hearing both sounds in <Dw>cl</Dw>, <Dw>st</Dw>, and <Dw>fr</Dw> without dropping one</span>],
      [<span key="a">Read longer short-vowel words</span>, <span key="b">four and five letters now, like <Dw>stomp</Dw> and <Dw>brush</Dw></span>],
      [<span key="a">Build early fluency</span>, <span key="b">rereading favorite pages until the words come quickly</span>],
    ],
    atHome: (
      <>
        Expect words to come out in chunks now instead of letter by letter, and expect blends to lose a sound
        here and there (<Dw>stop</Dw> read as <Dw>top</Dw> is classic and temporary). Rereading the same easy
        book until it sounds smooth is not a step backward; it is exactly how fluency gets built.
      </>
    ),
    booksReady: (
      <>
        Decodable readers with digraphs and blends, and any short book with a pattern they can ride. Keep a few
        too-easy favorites in rotation for confidence, and keep reading the rich stuff aloud.
      </>
    ),
    ctaLabel: 'See books for Word Builders',
  },
  3: {
    subhead: 'The code is starting to show its patterns.',
    nowWorkingOn: (
      <>Now working on: the silent <Dw>e</Dw> that turns <Dw>cap</Dw> into <Dw>cape</Dw>, and endings like{' '}
      <Dw>-ing</Dw> and <Dw>-ed</Dw>.</>
    ),
    opener: (
      <>
        Pattern Spotters have noticed the big secret: English isn&rsquo;t random. A quiet <Dw>e</Dw> at the end
        changes the vowel (<Dw>cake</Dw>, <Dw>bike</Dw>, <Dw>rope</Dw>), endings snap on and off, and longer
        words break into readable pieces.
      </>
    ),
    mastered: (
      <>
        Digraphs and blends are automatic, and short-vowel words of four and five letters read smoothly. The
        sounding-out machinery is solid; now it gets clever.
      </>
    ),
    buildingIntro: 'Pattern Spotters are learning to:',
    building: [
      [<span key="a">Use silent-e</span>, <span key="b">reading <Dw>kit</Dw> and <Dw>kite</Dw>, <Dw>hop</Dw> and <Dw>hope</Dw> as different words on sight</span>],
      [<span key="a">Read inflected endings</span>, <span key="b"><Dw>-ing</Dw>, <Dw>-ed</Dw>, and <Dw>-s</Dw> in words like <Dw>jumping</Dw> and <Dw>waved</Dw></span>],
      [<span key="a">Break two-syllable words apart</span>, <span key="b"><Dw>sunset</Dw>, <Dw>rabbit</Dw>, <Dw>basket</Dw>, one chunk at a time</span>],
      [<span key="a">Spot patterns in new words</span>, <span key="b">using a word they know to unlock one they don&rsquo;t</span>],
    ],
    atHome: (
      <>
        Expect occasional pattern over-reach (<Dw>have</Dw> read to rhyme with <Dw>gave</Dw> is a sign of
        learning, not a problem). When a long word stalls, cover part of it and reveal it chunk by chunk. The
        same word may need that help several times before it sticks; that is the repetition doing its job.
      </>
    ),
    booksReady: (
      <>
        Early reader series with short chapters start to fit here. Look for books with plenty of silent-e and
        ending patterns, and let them reread favorites; speed and expression grow on familiar ground.
      </>
    ),
    ctaLabel: 'See books for Pattern Spotters',
  },
  4: {
    subhead: 'Whole stories are within reach.',
    nowWorkingOn: (
      <>Now working on: vowel teams like <Dw>ai</Dw>, <Dw>oa</Dw>, and <Dw>ee</Dw> in words like <Dw>rain</Dw>,{' '}
      <Dw>boat</Dw>, and <Dw>light</Dw>.</>
    ),
    opener: (
      <>
        Story Readers can carry a whole story on their own. The frontier now is the trickiest part of the code:
        vowels that travel in pairs (<Dw>rain</Dw>, <Dw>boat</Dw>, <Dw>feet</Dw>) and teams that don&rsquo;t
        always play fair (<Dw>light</Dw>, <Dw>snow</Dw>, <Dw>book</Dw>).
      </>
    ),
    mastered: (
      <>
        Silent-e words, endings, and two-syllable words read automatically. Decoding effort has dropped enough
        that attention is going where it belongs: to the story.
      </>
    ),
    buildingIntro: 'Story Readers are learning to:',
    building: [
      [<span key="a">Read the common vowel teams</span>, <span key="b"><Dw>ai</Dw>, <Dw>ea</Dw>, <Dw>oa</Dw>, <Dw>ee</Dw> in words like <Dw>train</Dw> and <Dw>beach</Dw></span>],
      [<span key="a">Handle the tricky teams</span>, <span key="b"><Dw>igh</Dw>, <Dw>ow</Dw>, <Dw>oo</Dw>, where one spelling can make two sounds</span>],
      [<span key="a">Read longer words with confidence</span>, <span key="b">three syllables and beyond, like <Dw>remember</Dw> and <Dw>tomorrow</Dw></span>],
      [<span key="a">Build stamina</span>, <span key="b">staying with a longer book across several sittings</span>],
    ],
    atHome: (
      <>
        Expect mostly smooth reading with occasional stumbles on vowel teams (reading <Dw>bread</Dw> like{' '}
        <Dw>bead</Dw> is the classic). Trade pages or chapters aloud; hearing fluent reading right next to
        their own is one of the best fluency builders there is.
      </>
    ),
    booksReady: (
      <>
        Early chapter books and longer picture books are right in the zone. Series are gold at this stage:
        familiar characters and vocabulary carry them through book after book while the decoding polishes
        itself.
      </>
    ),
    ctaLabel: 'See books for Story Readers',
  },
  5: {
    subhead: 'The code is cracked. Now the world opens.',
    nowWorkingOn: (
      <>Now working on: r-controlled vowels (<Dw>card</Dw>, <Dw>fork</Dw>, <Dw>fern</Dw>) and the building
      blocks of bigger words.</>
    ),
    opener: (
      <>
        Book Adventurers read to learn and to get lost in a story. The remaining work is polish: vowels bossed
        around by <Dw>r</Dw>, word parts like <Dw>un-</Dw> and <Dw>-tion</Dw>, and the long words that come
        with big ideas.
      </>
    ),
    mastered: (
      <>
        The core code, from short vowels through vowel teams, reads automatically. Decoding is no longer the
        work; it is the vehicle.
      </>
    ),
    buildingIntro: 'Book Adventurers are learning to:',
    building: [
      [<span key="a">Read r-controlled vowels</span>, <span key="b"><Dw>ar</Dw>, <Dw>or</Dw>, <Dw>er</Dw>, <Dw>ir</Dw>, <Dw>ur</Dw> in words like <Dw>storm</Dw> and <Dw>bird</Dw></span>],
      [<span key="a">Use word parts</span>, <span key="b">prefixes and suffixes like <Dw>un-</Dw>, <Dw>re-</Dw>, <Dw>-ful</Dw>, <Dw>-tion</Dw> to unlock long words</span>],
      [<span key="a">Tackle multisyllabic words</span>, <span key="b"><Dw>important</Dw>, <Dw>celebration</Dw>, <Dw>mysterious</Dw>, chunk by chunk</span>],
      [<span key="a">Grow vocabulary from context</span>, <span key="b">meeting new words in books and keeping them</span>],
    ],
    atHome: (
      <>
        Expect independence, and protect it: time, light, and a steady supply of books they chose themselves.
        Keep talking about what they read; comprehension deepens in conversation. And keep reading aloud
        together when they will let you, because books above their reading level still feed their listening
        level.
      </>
    ),
    booksReady: (
      <>
        Chapter books, graphic novels, nonfiction deep-dives on whatever they currently love. Interest beats
        level now; a motivated reader will climb to the book.
      </>
    ),
    ctaLabel: 'See books for Book Adventurers',
  },
}

// ── The vertical named hopscotch court ──────────────────────────────────────

const COURT_POS_COUNT = 6

const COURT_SHORT = [
  'Sound\nExplorer',
  'Code\nCracker',
  'Word\nBuilder',
  'Pattern\nSpotter',
  'Story\nReader',
  'Book\nAdventurer',
]

function Sun() {
  return (
    <svg width="60" height="60" viewBox="-30 -30 60 60" aria-hidden="true">
      <circle r="13" fill="none" stroke="var(--yellow)" strokeWidth="2.5" />
      {Array.from({ length: 8 }).map((_, i) => {
        const a = (i * Math.PI) / 4
        return (
          <line
            key={i}
            x1={Math.cos(a) * 17}
            y1={Math.sin(a) * 17}
            x2={Math.cos(a) * 23}
            y2={Math.sin(a) * 23}
            stroke="var(--yellow)"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        )
      })}
    </svg>
  )
}

function VertCourt({ current }: { current: number }) {
  return (
    <div className="pr-court" aria-hidden="true">
      <div className="pr-court__sun">
        <Sun />
      </div>
      <div className="hs-eyebrow pr-court__start">START</div>
      {Array.from({ length: COURT_POS_COUNT }).map((_, i) => {
        const n = i + 1
        const state = n < current ? 'past' : n === current ? 'now' : 'future'
        const now = state === 'now'
        return (
          <div key={n} className={`pr-court__square pr-court__square--${n} pr-court__square--${state}`}>
            <span className={`pr-court__num pr-court__num--${state}`}>{n}</span>
            <span className={`pr-court__label pr-court__label--${state}`}>{COURT_SHORT[i]}</span>
            {state === 'past' && (
              <div className="pr-court__badge">
                <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="var(--ink)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 11l4 4 8-9" />
                </svg>
              </div>
            )}
            {now && (
              <svg width="34" height="28" viewBox="0 0 34 28" className="pr-court__beanbag">
                <ellipse cx="17" cy="15" rx="13" ry="10" fill="var(--ink)" />
                <path d="M9 13 Q17 9 25 13" fill="none" stroke="var(--yellow)" strokeWidth="1.8" strokeDasharray="2.5 3" strokeLinecap="round" />
              </svg>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Shared editorial pieces ─────────────────────────────────────────────────

function DateStamp({ date }: { date: Date }) {
  const formatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return (
    <div className="pr-date-stamp">
      <span className="hs-eyebrow pr-date-stamp__eyebrow">Reading placement</span>
      <span className="pr-date-stamp__value">{formatted}</span>
    </div>
  )
}

function Ornament() {
  return (
    <div className="pr-ornament">
      <span className="pr-ornament__line" />
      <svg width="11" height="11" viewBox="0 0 10 10" aria-hidden="true">
        <path d="M5 0 L6 4 L10 5 L6 6 L5 10 L4 6 L0 5 L4 4 Z" fill="var(--yellow)" />
      </svg>
      <span className="pr-ornament__line" />
    </div>
  )
}

function Body({ eyebrow, title, children }: { eyebrow: string; title: string; children: ReactNode }) {
  return (
    <section className="pr-body">
      <div className="hs-eyebrow pr-body__eyebrow">{eyebrow}</div>
      <h2 className="pr-body__title">{title}</h2>
      <div className="pr-body__content">{children}</div>
    </section>
  )
}

// ── Sound Explorer (stage 0) — no book games exist yet, so the report
// sets the expectation up front and swaps the book/upload CTAs for a
// "notify me" waitlist card (DEP-179). ────────────────────────────────────

const SE_NOTIFY_ANCHOR = 'se-notify'

function SoundExplorerStatusStrip() {
  return (
    <div className="pr-se-strip">
      <span className="pr-se-strip__icon">
        <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="10" cy="10" r="7.5" />
          <path d="M10 6v4l2.5 2" />
        </svg>
      </span>
      <div className="pr-se-strip__text">
        <strong>Book Games for Sound Explorers are coming soon.</strong>{' '}
        <span>The full reading evaluation is ready to read below.</span>
      </div>
      <a
        href={`#${SE_NOTIFY_ANCHOR}`}
        onClick={(e) => {
          e.preventDefault()
          document.getElementById(SE_NOTIFY_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }}
        className="pr-se-strip__cta"
      >
        Get notified
        <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="var(--yellow)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M10 4v12M5 11l5 5 5-5" />
        </svg>
      </a>
    </div>
  )
}

function SoundExplorerNotifyCard() {
  return (
    <section id={SE_NOTIFY_ANCHOR} className="pr-se-notify">
      <div className="pr-se-notify__card">
        <span className="pr-se-notify__deco-coral" />
        <span className="pr-se-notify__deco-yellow" />
        <div className="hs-eyebrow pr-se-notify__eyebrow">Coming soon</div>
        <h2 className="pr-se-notify__title">Sound Explorer Book Games are coming soon.</h2>
        <p className="pr-se-notify__body">
          This is the very beginning of the reading journey, a stage where the right kind of practice makes an
          outsized difference. We&rsquo;re building Book Games specifically for Sound Explorers, and we want to
          get them right before we ship. We&rsquo;ll email you when they&rsquo;re ready.
        </p>
        <div className="pr-se-notify__actions">
          <button type="button" className="hs-press pr-se-notify__btn">
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 6l7 5 7-5M3 6v9h14V6M3 6h14" />
            </svg>
            Notify me when they launch
          </button>
        </div>
      </div>
    </section>
  )
}

// ── The report ──────────────────────────────────────────────────────────────

export interface PlacementReportProps {
  learnerName: string
  /** Internal stage value 0–5 (Learner.readingStage / finalStage). */
  stageValue: number
  /** Memorizer pattern detected during placement → the personal card. */
  memorizerFlag?: boolean
  placedAt?: Date
  onSeeBooks: () => void
  onUploadBook: () => void
  /** When provided, a quiet "Retake the placement" control renders at the
   * end of the report (with an inline confirm — kids grow, placements
   * shouldn't be wiped by a stray tap). */
  onRetake?: () => void
}

export function PlacementReport({
  learnerName,
  stageValue,
  memorizerFlag = false,
  placedAt,
  onSeeBooks,
  onUploadBook,
  onRetake,
}: PlacementReportProps) {
  const [confirmRetake, setConfirmRetake] = useState(false)
  const clamped = Math.min(5, Math.max(0, stageValue))
  const courtCurrent = clamped + 1 // court squares are 1-based
  const label = stages[clamped]?.label as StageLabel
  const stageName = STAGE_DISPLAY[label]?.name ?? 'Reader'
  const content = STAGE_CONTENT[clamped]
  // Sound Explorer (stage 0): no book games exist yet — the status strip,
  // hero CTAs, and closing section all differ (DEP-179).
  const isSoundExplorer = clamped === 0

  const arrow = (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 10h13M11 4l6 6-6 6" />
    </svg>
  )

  return (
    <div className="hs-brand pr-root">
      <Grain />

      {/* ── status strip: sets the expectation up front, points to the notify card ── */}
      {isSoundExplorer && <SoundExplorerStatusStrip />}

      {/* ── hero: court + identity ── */}
      <section className="pr-hero">
        <div className="pr-hero__stamp">
          <DateStamp date={placedAt ?? new Date()} />
        </div>
        <div className="pr-hero__inner">
          <VertCourt current={courtCurrent} />
          <div className="pr-hero__identity">
            <div className="hs-eyebrow pr-hero__eyebrow">
              <MaskedName>{learnerName}</MaskedName>&rsquo;s reading journey · stage {courtCurrent} of 6
            </div>
            <h1 className="pr-hero__title">
              <MaskedName>{learnerName}</MaskedName> is a
              <br />
              <span className="pr-hero__highlight">
                <span aria-hidden="true" className="pr-hero__highlight-swipe" />
                <span className="pr-hero__highlight-text">{stageName}</span>
              </span>
              .
            </h1>
            <p className="pr-hero__subhead">{content.subhead}</p>
            <p className="pr-hero__now-working">{content.nowWorkingOn}</p>
            {!isSoundExplorer && (
              <div className="pr-hero__ctas">
                <button type="button" className="hs-press pr-cta" onClick={onSeeBooks}>
                  See books for <MaskedName>{learnerName}</MaskedName>
                  {arrow}
                </button>
                <button
                  type="button"
                  className="hs-press hs-press--paper pr-cta pr-cta--paper"
                  onClick={onUploadBook}
                >
                  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M10 15V4M5 9l5-5 5 5M4 16h12" />
                  </svg>
                  Upload a book
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── opener ── */}
      <section className="pr-opener">
        <p className="pr-opener__text">{content.opener}</p>
        <div className="pr-opener__ornament">
          <Ornament />
        </div>
      </section>

      <Body eyebrow="What's solid" title="Skills they've mastered">
        <p>{content.mastered}</p>
      </Body>

      <Body eyebrow="The work of this stage" title="Skills they're building">
        <p className="pr-body__intro">{content.buildingIntro}</p>
        <ul className="pr-building-list">
          {content.building.map(([head, body], i) => (
            <li key={i} className="pr-building-item">
              <span className="pr-building-dot" />
              <span>
                <strong>{head}</strong>, {body}
              </span>
            </li>
          ))}
        </ul>
        <p className="pr-building-note">
          These are the headliners. Other pieces click into place around them, and <MaskedName>{learnerName}</MaskedName> will meet them
          across their Hopscotch practice.
        </p>
      </Body>

      {/* ── the personal card (memorizer pattern) ── */}
      {memorizerFlag && (
        <section className="pr-memorizer">
          <div className="pr-memorizer__card">
            <div className="pr-memorizer__tag">From your placement</div>
            <h2 className="pr-memorizer__title">What we noticed about <MaskedName>{learnerName}</MaskedName></h2>
            <div className="pr-memorizer__body">
              <p>
                During the placement, <MaskedName>{learnerName}</MaskedName> read familiar short words confidently but had a harder time
                with made-up words like <Dw>nud</Dw> and <Dw>zib</Dw>. That pattern often means the brain is
                recognizing whole words by shape rather than fully sounding each one out (common for kids who
                learned to read partly by sight).
              </p>
              <div className="pr-memorizer__block">
                <div className="pr-memorizer__subhead">What to watch for at home</div>
                <p>
                  <MaskedName>{learnerName}</MaskedName> may read a word by guessing from the first letter or two, especially with
                  familiar-looking words. When you notice this, gently ask:{' '}
                  <span className="hs-hand pr-memorizer__quote">&ldquo;Can you sound it out for me?&rdquo;</span>{' '}
                  That single cue helps the brain switch from recognition to decoding.
                </p>
              </div>
              <div className="pr-memorizer__block pr-memorizer__block--tight">
                <div className="pr-memorizer__subhead">What Hopscotch is built for</div>
                <p>
                  Hopscotch&rsquo;s games put sound-by-sound work in the driver&rsquo;s seat: building short
                  words from letter tiles, sounding out each letter. That&rsquo;s the practice that strengthens
                  decoding when reading has leaned on memory.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      <Body eyebrow="At home" title="What to expect">
        <p>{content.atHome}</p>
      </Body>

      <Body eyebrow="The right books, right now" title="What they're ready for">
        <p>{content.booksReady}</p>
      </Body>

      {/* ── closing: notify card (Sound Explorer) or the normal books CTA ── */}
      {isSoundExplorer ? (
        <div className="pr-se-closing">
          <SoundExplorerNotifyCard />
        </div>
      ) : (
        <section className="pr-closing">
          <button type="button" className="hs-press pr-cta pr-cta--large" onClick={onSeeBooks}>
            {content.ctaLabel}
            {arrow}
          </button>
          <p className="pr-closing__note">
            Or upload any book they&rsquo;re curious about, and we&rsquo;ll build games for it.
          </p>
        </section>
      )}

      <section className="pr-retake">
        {/* Quiet retake — dashed-link vocabulary, never competes with the
            coral CTA. Inline confirm replaces the link on first tap. */}
        {onRetake && (
          <div className="pr-retake__inner">
            {confirmRetake ? (
              <div className="pr-retake__confirm">
                <p className="pr-retake__confirm-text">
                  Retake the placement? <MaskedName>{learnerName}</MaskedName>&rsquo;s current placement will be replaced with
                  the new result.
                </p>
                <div className="pr-retake__confirm-actions">
                  {/* paper press, not blue — DS v1.2: blue is never buttons */}
                  <button
                    type="button"
                    className="hs-press hs-press--paper pr-retake__confirm-btn"
                    onClick={onRetake}
                  >
                    Yes, retake it
                  </button>
                  <button type="button" onClick={() => setConfirmRetake(false)} className="pr-retake__cancel-btn">
                    Keep this one
                  </button>
                </div>
              </div>
            ) : (
              <p className="pr-retake__prompt">
                Kids grow fast.{' '}
                <button type="button" onClick={() => setConfirmRetake(true)} className="pr-retake__link-btn">
                  Retake the placement
                </button>{' '}
                any time.
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  )
}
